import { GenericItem, InputItemGroup, ItemsProp } from '../types';

const itemProps = [
  'selectedItem',
  'selectedItems',
  'defaultSelectedItem',
  'defaultSelectedItems',
  'initialSelectedItem',
  'initialSelectedItems',
];

const checkItems = <TItem extends GenericItem>({
  itemsToCheck,
  allItems,
  paramName,
}: {
  itemsToCheck: TItem | TItem[] | null | undefined;
  allItems: TItem[];
  paramName: string;
}) => {
  if (!itemsToCheck) {
    return;
  }

  const itemsAsArray = Array.isArray(itemsToCheck)
    ? itemsToCheck
    : [itemsToCheck];

  const invalidItems = itemsAsArray.filter((item) => !allItems.includes(item));

  if (invalidItems.length > 0 && process.env.NODE_ENV !== 'production') {
    const multiple = invalidItems.length > 1;

    console.warn(
      '@adsk/alloy-react-dropdown:',
      `item${multiple ? 's' : ''} provided to`,
      `'${paramName}'`,
      `${
        multiple ? 'were' : 'was'
      } not referentially equal to any item in 'items' prop`,
      invalidItems,
    );
  }
};

const isItem = <TItem extends GenericItem>(
  item: TItem | InputItemGroup<TItem>,
): item is TItem => !(item as InputItemGroup<TItem>).items;

export default <TItem extends GenericItem>({
  itemToKey,
  items,
  ...props
}: {
  selectedItem?: TItem | null;
  selectedItems?: TItem[] | null;
  defaultSelectedItem?: TItem | null;
  defaultSelectedItems?: TItem[] | null;
  initialSelectedItem?: TItem | null;
  initialSelectedItems?: TItem[] | null;
  items: ItemsProp<TItem>;
  itemToKey?: (item: TItem) => string;
}) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  if (itemToKey !== undefined) {
    // if itemToKey is provided, the user is overriding the reference equality behaviour, so no need to check
    // https://github.com/downshift-js/downshift/blob/master/src/hooks/useCombobox/README.md#itemtokey
    return;
  }

  const allItems = (items as (TItem | InputItemGroup<TItem>)[]).reduce(
    (acc, curr) => {
      if (isItem(curr)) {
        acc.push(curr);
        return acc;
      } else {
        return [...acc, ...curr.items];
      }
    },
    [] as TItem[],
  );

  Object.keys(props)
    .filter((key) => itemProps.includes(key))
    .forEach((paramName) => {
      checkItems({
        itemsToCheck: props[paramName as keyof typeof props],
        allItems,
        paramName,
      });
    });
};
